import type { Editor } from '@tiptap/react';

import { isEmptyString } from '@repo/lib/assertion';
import { useEditorState } from '@tiptap/react';
import { CheckCircle2, TableIcon } from 'lucide-react';
import { useCallback, useState } from 'react';

import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button/ButtonGroup';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useTiptapEditor } from '@/hooks/tiptap';
import { hasExtension, isNodeInSchema, shouldShow } from '@/lib/tiptap';

import type { InsertTablePopoverProps } from '.';

function isTableInsertDisabled(editor: Editor | null, userDisabled: boolean = false): boolean {
  if (!editor || userDisabled) return true;
  if (editor.isActive('table')) return true;
  if (!hasExtension(editor, 'table')) return true;
  return !editor.can().insertTable();
}

function clampTableInputValue(value: string, min: number = 1, max: number = 12): string {
  if (isEmptyString(value)) return value;
  const num = Number(value);
  if (num < min) return String(min);
  if (num > max) return String(max);
  return value;
}

function insertTable(editor: Editor | null, rows: number, cols: number): boolean {
  if (!editor) return false;
  return editor.chain().focus().insertTable({ rows, cols }).run();
}

function useTableActive(editor: Editor | null): boolean {
  return (
    useEditorState({
      editor,
      selector: (ctx) => ctx.editor?.isActive('table'),
    }) ?? false
  );
}

function useTableExtension(editor: Editor | null, disabled?: boolean) {
  const isDisabled: boolean = isTableInsertDisabled(editor, disabled);
  const isActive: boolean = useTableActive(editor);

  const [rows, setRows] = useState<string>('3');
  const [cols, setCols] = useState<string>('3');

  const handleRowsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value: string = clampTableInputValue(e.target.value);
    setRows(value);
  };

  const handleColsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value: string = clampTableInputValue(e.target.value);
    setCols(value);
  };

  const handleInsertTable = () => {
    if (rows && cols && isDisabled) return;
    insertTable(editor, Number(rows), Number(cols));
  };

  const isCorrectContent: boolean = isEmptyString(rows) || isEmptyString(cols);

  return {
    Icon: TableIcon,
    isDisabled,
    isActive,
    rows,
    cols,
    isCorrectContent,
    handleRowsChange,
    handleColsChange,
    handleInsertTable,
  };
}

export function InsertTablePopover(insertTablePopoverProps: InsertTablePopoverProps) {
  const { editor: providedEditor, onOpenChange, disabled, children, ...props } = insertTablePopoverProps;
  const editor: Editor | null = useTiptapEditor(providedEditor);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const { Icon, isDisabled, isActive, rows, cols, isCorrectContent, handleRowsChange, handleColsChange, handleInsertTable } = useTableExtension(editor, disabled);
  const inSchema: boolean = isNodeInSchema('table', editor);
  const show: boolean = shouldShow([!!editor, inSchema]);

  const handleOnOpenChange = useCallback(
    (nextOpen: boolean) => {
      onOpenChange?.(nextOpen);
      setIsOpen(nextOpen);
    },
    [disabled, onOpenChange]
  );
  if (!show || !editor || !editor.isEditable) {
    return null;
  }

  return (
    <Popover open={isOpen} onOpenChange={handleOnOpenChange}>
      <PopoverTrigger asChild>
        <Button type="button" data-slot="insert-table-button" disabled={isDisabled} data-active-state={isActive ? 'on' : 'off'} data-disabled={isDisabled} aria-pressed={isActive} {...props}>
          {children || <Icon />}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="border-0 p-0 shadow-lg">
        <ButtonGroup className="w-full">
          <Input placeholder="3" type="number" aria-label="rows" min={1} max={12} value={rows} onChange={handleRowsChange} />
          <Input placeholder="3" type="number" aria-label="columns" min={1} max={12} value={cols} onChange={handleColsChange} />
          <Button variant={'outline'} disabled={isDisabled || isCorrectContent} onClick={handleInsertTable}>
            <CheckCircle2 />
          </Button>
        </ButtonGroup>
      </PopoverContent>
    </Popover>
  );
}
